(() => {
  if (window.__bnLmsRealRuntimeV1) return;
  window.__bnLmsRealRuntimeV1 = true;
  'use strict';

  const runtimes = new WeakMap();

  const SELECTORS = {
    loading: '[data-bn-lms-loading]',
    loggedOut: '[data-bn-lms-logged-out]',
    noAccess: '[data-bn-lms-no-access]',
    error: '[data-bn-lms-error]',
    errorMessage: '[data-bn-lms-error-message]',
    retry: '[data-bn-lms-retry]',
    workspace: '[data-bn-lms-workspace]',
    notice: '[data-bn-lms-notice]',
    status: '[data-bn-lms-status]',
    stats: '[data-bn-lms-stats]',
    statCourses: '[data-bn-lms-stat-courses]',
    statModules: '[data-bn-lms-stat-modules]',
    statLessons: '[data-bn-lms-stat-lessons]',
    statResources: '[data-bn-lms-stat-resources]',
    courseList: '[data-bn-lms-course-list]',
    courseLoading: '[data-bn-lms-course-loading]',
    courseContent: '[data-bn-lms-course-content]',
    courseTitle: '[data-bn-lms-course-title]',
    courseDescription: '[data-bn-lms-course-description]',
    courseModality: '[data-bn-lms-course-modality]',
    courseLevel: '[data-bn-lms-course-level]',
    emptyCourse: '[data-bn-lms-empty-course]',
    modules: '[data-bn-lms-modules]',
    generalResources: '[data-bn-lms-general-resources]',
    generalResourceList: '[data-bn-lms-general-resource-list]',
    progress: '[data-bn-lms-progress]',
    progressPercent: '[data-bn-lms-progress-percent]',
    progressCount: '[data-bn-lms-progress-count]',
    progressTrack: '[data-bn-lms-progress-track]',
    progressBar: '[data-bn-lms-progress-bar]',
    continueButton: '[data-bn-lms-continue]',
  };

  const toArray = (value) => (Array.isArray(value) ? value : []);
  const toText = (value, fallback = '') => (typeof value === 'string' ? value.trim() : fallback);
  const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(toNumber(value))));

  const setHidden = (element, hidden) => {
    if (element) element.hidden = hidden;
  };

  const setText = (element, value) => {
    if (element) element.textContent = value;
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const normalizeLabel = (value) => {
    const text = toText(value);
    if (!text) return 'Contenido';
    return text.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const safeUrl = (value) => {
    const raw = toText(value);
    if (!raw) return null;

    try {
      const url = new URL(raw, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      return url.href;
    } catch (error) {
      return null;
    }
  };

  const getEmbedUrl = (value) => {
    const href = safeUrl(value);
    if (!href) return null;

    const url = new URL(href);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname.startsWith('/embed/')) return href;
      const videoId = url.searchParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` : null;
    }

    if (host === 'youtu.be') {
      const videoId = url.pathname.split('/').filter(Boolean)[0];
      return videoId ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` : null;
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const videoId = url.pathname.split('/').filter(Boolean).pop();
      return videoId ? `https://player.vimeo.com/video/${encodeURIComponent(videoId)}` : null;
    }

    return null;
  };

  const sanitizeHtml = (html) => {
    const value = toText(html);
    if (!value) return '';

    const template = document.createElement('template');
    template.innerHTML = value;

    template.content.querySelectorAll('script, style, iframe, object, embed, form').forEach((node) => node.remove());
    template.content.querySelectorAll('*').forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const attrValue = attribute.value.trim().toLowerCase();
        if (name.startsWith('on') || (['href', 'src'].includes(name) && attrValue.startsWith('javascript:'))) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    return template.innerHTML;
  };

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');
  };

  class BnLmsRequestError extends Error {
    constructor(message, { status = 0, payload = null } = {}) {
      super(message);
      this.name = 'BnLmsRequestError';
      this.status = status;
      this.payload = payload;
    }
  }

  const requestJson = async (url, options = {}) => {
    const headers = new Headers(options.headers || {});
    headers.set('Accept', 'application/json');

    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new BnLmsRequestError(`Respuesta inesperada del servidor (${response.status}).`, {
        status: response.status,
      });
    }

    const payload = await response.json();
    if (!response.ok || payload?.ok === false) {
      throw new BnLmsRequestError(
        toText(payload?.message) || `No fue posible completar la solicitud (${response.status}).`,
        {
          status: response.status,
          payload,
        },
      );
    }

    return payload;
  };

  const getErrorPayload = (error) =>
    error instanceof BnLmsRequestError && error.payload && typeof error.payload === 'object'
      ? error.payload
      : {};

  const getErrorStatus = (error) =>
    error instanceof BnLmsRequestError ? toNumber(error.status) : 0;

  const getErrorCode = (error) =>
    toText(getErrorPayload(error)?.code || getErrorPayload(error)?.error).toLowerCase();

  const isLoggedOutError = (error) => {
    const payload = getErrorPayload(error);
    const code = getErrorCode(error);

    return (
      getErrorStatus(error) === 401 ||
      payload?.loggedIn === false ||
      ['customer_login_required', 'login_required', 'not_logged_in', 'unauthenticated'].includes(code)
    );
  };

  const isNoAccessError = (error) => {
    const payload = getErrorPayload(error);
    const code = getErrorCode(error);

    return (
      getErrorStatus(error) === 403 ||
      payload?.accessCount === 0 ||
      [
        'academic_access_required',
        'access_denied',
        'course_access_required',
        'no_access',
        'no_courses',
        'student_access_required',
      ].includes(code)
    );
  };

  const createResourceCard = (resource) => {
    const article = createElement('article', 'bn-lms-real__resource');
    const icon = createElement('span', 'bn-lms-real__resource-icon', resource?.isDownloadable ? '↓' : '↗');
    icon.setAttribute('aria-hidden', 'true');

    const copy = createElement('div', 'bn-lms-real__resource-copy');
    const type = createElement('span', 'bn-lms-real__resource-type', normalizeLabel(resource?.resourceType));
    const title = createElement('h4', '', toText(resource?.title, 'Recurso'));
    copy.append(type, title);

    const description = toText(resource?.description);
    if (description) copy.append(createElement('p', '', description));

    const href = safeUrl(resource?.url);
    if (href) {
      const link = createElement('a', 'bn-lms-real__resource-link', resource?.isDownloadable ? 'Descargar' : 'Abrir recurso');
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.append(createElement('span', '', ' →'));
      copy.append(link);
    } else {
      copy.append(createElement('span', 'bn-lms-real__resource-pending', 'Recurso sin enlace disponible'));
    }

    article.append(icon, copy);
    return article;
  };

  const normalizeProgress = (progress, lessonCount = 0) => ({
    totalLessons: Math.max(0, toNumber(progress?.totalLessons || lessonCount)),
    completedLessons: Math.max(0, toNumber(progress?.completedLessons)),
    progressPercent: clampPercent(progress?.progressPercent),
    status: toText(progress?.status, 'not_started'),
  });

  const isLessonCompleted = (lesson) =>
    toText(lesson?.progress?.status).toLowerCase() === 'completed' ||
    clampPercent(lesson?.progress?.progressPercent) >= 100;

  const initRoot = (root) => {
    if (!root || root.dataset.bnLmsRealMounted === 'true') return;
    root.dataset.bnLmsRealMounted = 'true';
    const lifecycleController = new AbortController();
    const { signal } = lifecycleController;

    const elements = Object.fromEntries(
      Object.entries(SELECTORS).map(([key, selector]) => [key, root.querySelector(selector)]),
    );

    const labels = {
      pending: toText(root.dataset.progressPendingLabel, 'Pendiente'),
      completed: toText(root.dataset.progressCompletedLabel, 'Completada'),
      mark: toText(root.dataset.progressMarkLabel, 'Marcar como completada'),
      unmark: toText(root.dataset.progressUnmarkLabel, 'Desmarcar completada'),
      saving: toText(root.dataset.progressSavingLabel, 'Guardando...'),
      success: toText(root.dataset.progressSuccessLabel, 'Progreso actualizado correctamente.'),
      continue: toText(root.dataset.progressContinueLabel, 'Continuar curso'),
    };

    const state = {
      courses: [],
      activeSlug: '',
      activeCourse: null,
      progressAvailable: false,
      requestId: 0,
      noticeTimer: null,
      savingLessons: new Set(),
      coursesController: null,
      courseController: null,
      focusTimer: null,
    };

    const setStatus = (text, mode = 'loading') => {
      setText(elements.status, text);
      root.dataset.bnLmsState = mode;
    };

    const showNotice = (message, mode = 'success') => {
      if (!elements.notice) return;
      window.clearTimeout(state.noticeTimer);
      elements.notice.dataset.noticeMode = mode;
      setText(elements.notice, message);
      setHidden(elements.notice, false);
      state.noticeTimer = window.setTimeout(() => setHidden(elements.notice, true), 5200);
    };

    const hidePrimaryStates = () => {
      setHidden(elements.loading, true);
      setHidden(elements.loggedOut, true);
      setHidden(elements.noAccess, true);
      setHidden(elements.error, true);
      setHidden(elements.workspace, true);
      setHidden(elements.stats, true);
    };

    const showLoggedOut = () => {
      hidePrimaryStates();
      setHidden(elements.loggedOut, false);
      setStatus('Sesión requerida', 'locked');
    };

    const showNoAccess = () => {
      hidePrimaryStates();
      setHidden(elements.noAccess, false);
      setStatus('Acceso académico pendiente', 'locked');
    };

    const showError = (error) => {
      hidePrimaryStates();
      setHidden(elements.error, false);
      setText(elements.errorMessage, error instanceof Error ? error.message : 'No fue posible cargar tu academia.');
      setStatus('Error de conexión', 'error');
    };

    const flattenLessons = (course) =>
      toArray(course?.modules).flatMap((module) => toArray(module?.lessons));

    const updateStats = (course) => {
      const modules = toArray(course?.modules);
      const lessons = flattenLessons(course);
      const resources = [
        ...toArray(course?.resources),
        ...modules.flatMap((module) => toArray(module?.resources)),
        ...lessons.flatMap((lesson) => toArray(lesson?.resources)),
      ];

      setText(elements.statCourses, String(state.courses.length));
      setText(elements.statModules, String(modules.length));
      setText(elements.statLessons, String(lessons.length));
      setText(elements.statResources, String(resources.length));
      setHidden(elements.stats, false);
    };

    const updateProgressCard = (course) => {
      const lessonCount = flattenLessons(course).length;
      const progress = normalizeProgress(course?.progress, lessonCount);
      const showProgress = state.progressAvailable && progress.totalLessons > 0;

      setHidden(elements.progress, !showProgress);
      if (!showProgress) return;

      setText(elements.progressPercent, `${progress.progressPercent}%`);
      setText(
        elements.progressCount,
        `${progress.completedLessons} de ${progress.totalLessons} lecciones completadas`,
      );

      if (elements.progressBar) {
        elements.progressBar.style.width = `${progress.progressPercent}%`;
      }

      if (elements.progressTrack) {
        elements.progressTrack.setAttribute('aria-valuenow', String(progress.progressPercent));
        elements.progressTrack.setAttribute(
          'aria-label',
          `${progress.progressPercent}% del curso completado`,
        );
      }

      if (elements.continueButton) {
        const allCompleted = progress.totalLessons > 0 && progress.completedLessons >= progress.totalLessons;
        elements.continueButton.textContent = allCompleted ? 'Curso completado ✓' : `${labels.continue} →`;
        elements.continueButton.dataset.courseCompleted = allCompleted ? 'true' : 'false';
      }
    };

    const updateActiveCourseButton = () => {
      elements.courseList?.querySelectorAll('[data-course-slug]').forEach((button) => {
        const active = button.dataset.courseSlug === state.activeSlug;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'page' : 'false');
      });
    };

    const renderCourseList = () => {
      if (!elements.courseList) return;
      elements.courseList.replaceChildren();

      state.courses.forEach((course) => {
        const button = createElement('button', 'bn-lms-real__course-button');
        button.type = 'button';
        button.dataset.courseSlug = toText(course?.slug);

        const copy = createElement('span', 'bn-lms-real__course-button-copy');
        copy.append(createElement('strong', '', toText(course?.title, 'Curso')));

        const progress = normalizeProgress(course?.progress, toNumber(course?.publishedLessons));
        const summaryParts = [
          `${toNumber(course?.publishedModules)} módulos`,
          `${toNumber(course?.publishedLessons)} lecciones`,
        ];
        if (state.progressAvailable && progress.totalLessons > 0) {
          summaryParts.push(`${progress.progressPercent}% completado`);
        }
        copy.append(createElement('small', '', summaryParts.join(' · ')));

        const arrow = createElement('span', 'bn-lms-real__course-button-arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        button.append(copy, arrow);
        button.addEventListener('click', () => loadCourse(button.dataset.courseSlug || ''));
        elements.courseList.append(button);
      });

      updateActiveCourseButton();
    };

    const updateLessonDom = (lesson) => {
      const lessonSlug = toText(lesson?.slug);
      if (!lessonSlug || !elements.modules) return;

      const article = elements.modules.querySelector(
        `[data-lesson-slug="${cssEscape(lessonSlug)}"]`,
      );
      if (!article) return;

      const completed = isLessonCompleted(lesson);
      article.dataset.lessonStatus = completed ? 'completed' : 'not_started';
      article.classList.toggle('is-completed', completed);

      const badge = article.querySelector('[data-bn-lms-lesson-status]');
      const button = article.querySelector('[data-bn-lms-progress-toggle]');
      setText(badge, completed ? labels.completed : labels.pending);
      badge?.classList.toggle('is-completed', completed);

      if (button) {
        button.dataset.completed = completed ? 'true' : 'false';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        setText(button, completed ? labels.unmark : labels.mark);
      }
    };

    const saveLessonProgress = async (lesson) => {
      const lessonSlug = toText(lesson?.slug);
      const courseSlug = toText(state.activeCourse?.slug);
      if (!lessonSlug || !courseSlug || state.savingLessons.has(lessonSlug)) return;

      const completed = !isLessonCompleted(lesson);
      const article = elements.modules?.querySelector(
        `[data-lesson-slug="${cssEscape(lessonSlug)}"]`,
      );
      const button = article?.querySelector('[data-bn-lms-progress-toggle]');

      state.savingLessons.add(lessonSlug);
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        setText(button, labels.saving);
      }

      try {
        const endpoint = root.dataset.progressEndpoint || '/apps/brillo-nativo/lms/progress';
        const formBody = new URLSearchParams();
        formBody.set('courseSlug', courseSlug);
        formBody.set('lessonSlug', lessonSlug);
        formBody.set('completed', completed ? 'true' : 'false');

        const payload = await requestJson(endpoint, {
          method: 'POST',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          body: formBody.toString(),
          signal,
        });

        lesson.progress = payload?.lessonProgress || {
          status: completed ? 'completed' : 'not_started',
          progressPercent: completed ? 100 : 0,
        };
        state.activeCourse.progress = payload?.courseProgress || state.activeCourse.progress;

        const courseSummary = state.courses.find((course) => toText(course?.slug) === courseSlug);
        if (courseSummary) courseSummary.progress = state.activeCourse.progress;

        updateLessonDom(lesson);
        updateProgressCard(state.activeCourse);
        renderCourseList();
        showNotice(toText(payload?.message, labels.success), 'success');
        setStatus('Academia conectada', 'ready');
      } catch (error) {
        if (error?.name === 'AbortError' || signal.aborted) return;
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          setText(button, isLessonCompleted(lesson) ? labels.unmark : labels.mark);
        }

        if (isLoggedOutError(error)) {
          showLoggedOut();
          return;
        }

        if (isNoAccessError(error)) {
          showNoAccess();
          return;
        }

        showNotice(
          error instanceof Error ? error.message : 'No fue posible guardar el progreso.',
          'error',
        );
      } finally {
        state.savingLessons.delete(lessonSlug);
      }
    };

    const createLesson = (lesson, index) => {
      const article = createElement('article', 'bn-lms-real__lesson');
      const lessonSlug = toText(lesson?.slug, `lesson-${index + 1}`);
      const completed = isLessonCompleted(lesson);
      article.dataset.lessonSlug = lessonSlug;
      article.dataset.lessonStatus = completed ? 'completed' : 'not_started';
      article.classList.toggle('is-completed', completed);
      article.tabIndex = -1;

      const head = createElement('div', 'bn-lms-real__lesson-head');
      const number = createElement('span', 'bn-lms-real__lesson-number', String(index + 1).padStart(2, '0'));
      const heading = createElement('div', 'bn-lms-real__lesson-heading');
      const meta = createElement('div', 'bn-lms-real__lesson-meta');
      meta.append(createElement('span', '', normalizeLabel(lesson?.lessonType)));

      const duration = toNumber(lesson?.durationSeconds);
      if (duration > 0) {
        const minutes = Math.max(1, Math.round(duration / 60));
        meta.append(createElement('span', '', `${minutes} min`));
      }

      if (lesson?.isRequired) meta.append(createElement('span', '', 'Obligatoria'));
      if (lesson?.isPreview) meta.append(createElement('span', '', 'Vista previa'));

      const title = createElement('h4', '', toText(lesson?.title, `Lección ${index + 1}`));
      heading.append(meta, title);

      const progressControls = createElement('div', 'bn-lms-real__lesson-progress');
      const status = createElement(
        'span',
        `bn-lms-real__lesson-status${completed ? ' is-completed' : ''}`,
        completed ? labels.completed : labels.pending,
      );
      status.dataset.bnLmsLessonStatus = '';
      progressControls.append(status);

      if (state.progressAvailable) {
        const toggle = createElement(
          'button',
          'bn-lms-real__lesson-toggle',
          completed ? labels.unmark : labels.mark,
        );
        toggle.type = 'button';
        toggle.dataset.bnLmsProgressToggle = '';
        toggle.dataset.completed = completed ? 'true' : 'false';
        toggle.addEventListener('click', () => saveLessonProgress(lesson));
        progressControls.append(toggle);
      }

      head.append(number, heading, progressControls);
      article.append(head);

      const summary = toText(lesson?.summary);
      if (summary) article.append(createElement('p', 'bn-lms-real__lesson-summary', summary));

      const embedUrl = getEmbedUrl(lesson?.videoUrl);
      const videoUrl = safeUrl(lesson?.videoUrl);
      if (embedUrl) {
        const frameWrap = createElement('div', 'bn-lms-real__video');
        const frame = document.createElement('iframe');
        frame.src = embedUrl;
        frame.title = toText(lesson?.title, 'Video de la lección');
        frame.loading = 'lazy';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.allowFullscreen = true;
        frameWrap.append(frame);
        article.append(frameWrap);
      } else if (videoUrl) {
        const videoLink = createElement('a', 'bn-lms-real__button bn-lms-real__button--secondary', 'Abrir video →');
        videoLink.href = videoUrl;
        videoLink.target = '_blank';
        videoLink.rel = 'noopener noreferrer';
        article.append(videoLink);
      }

      const contentHtml = sanitizeHtml(lesson?.contentHtml);
      if (contentHtml) {
        const content = createElement('div', 'bn-lms-real__lesson-content rte');
        content.innerHTML = contentHtml;
        article.append(content);
      }

      const resources = toArray(lesson?.resources);
      if (resources.length) {
        const section = createElement('section', 'bn-lms-real__lesson-resources');
        section.append(createElement('h5', '', 'Recursos de esta lección'));
        const grid = createElement('div', 'bn-lms-real__resource-grid');
        resources.forEach((resource) => grid.append(createResourceCard(resource)));
        section.append(grid);
        article.append(section);
      }

      return article;
    };

    const createModule = (module, index) => {
      const details = createElement('details', 'bn-lms-real__module');
      details.dataset.moduleSlug = toText(module?.slug, `module-${index + 1}`);
      if (index === 0) details.open = true;

      const summary = createElement('summary', 'bn-lms-real__module-summary');
      const number = createElement('span', 'bn-lms-real__module-number', String(index + 1).padStart(2, '0'));
      const copy = createElement('div', 'bn-lms-real__module-copy');
      copy.append(createElement('span', 'bn-lms-real__module-kicker', 'Módulo'));
      copy.append(createElement('h3', '', toText(module?.title, `Módulo ${index + 1}`)));

      const description = toText(module?.description);
      if (description) copy.append(createElement('p', '', description));

      const lessons = toArray(module?.lessons);
      const completedCount = lessons.filter(isLessonCompleted).length;
      const countText = state.progressAvailable && lessons.length
        ? `${completedCount} de ${lessons.length} completadas`
        : `${lessons.length} lecciones`;
      const count = createElement('span', 'bn-lms-real__module-count', countText);
      summary.append(number, copy, count);
      details.append(summary);

      const body = createElement('div', 'bn-lms-real__module-body');
      const resources = toArray(module?.resources);

      if (lessons.length) {
        const lessonList = createElement('div', 'bn-lms-real__lesson-list');
        lessons.forEach((lesson, lessonIndex) => lessonList.append(createLesson(lesson, lessonIndex)));
        body.append(lessonList);
      } else {
        body.append(createElement('p', 'bn-lms-real__module-empty', 'Este módulo no tiene lecciones publicadas todavía.'));
      }

      if (resources.length) {
        const section = createElement('section', 'bn-lms-real__module-resources');
        section.append(createElement('h4', '', 'Recursos del módulo'));
        const grid = createElement('div', 'bn-lms-real__resource-grid');
        resources.forEach((resource) => grid.append(createResourceCard(resource)));
        section.append(grid);
        body.append(section);
      }

      details.append(body);
      return details;
    };

    const renderCourse = (course) => {
      state.activeCourse = course;
      setText(elements.courseTitle, toText(course?.title, 'Curso'));
      setText(elements.courseDescription, toText(course?.description, 'Contenido académico disponible para tu cuenta.'));
      setText(elements.courseModality, normalizeLabel(course?.modality));
      setText(elements.courseLevel, normalizeLabel(course?.level));

      const modules = toArray(course?.modules);
      const generalResources = toArray(course?.resources);

      elements.modules?.replaceChildren();
      modules.forEach((module, index) => elements.modules?.append(createModule(module, index)));

      elements.generalResourceList?.replaceChildren();
      generalResources.forEach((resource) => elements.generalResourceList?.append(createResourceCard(resource)));
      setHidden(elements.generalResources, generalResources.length === 0);

      const hasContent = modules.length > 0 || generalResources.length > 0;
      setHidden(elements.emptyCourse, hasContent);
      updateProgressCard(course);
      setHidden(elements.courseContent, false);
      updateStats(course);
    };

    const loadCourse = async (slug) => {
      const cleanSlug = toText(slug);
      if (!cleanSlug) return;

      state.activeSlug = cleanSlug;
      updateActiveCourseButton();
      setHidden(elements.courseContent, true);
      setHidden(elements.courseLoading, false);
      setStatus('Cargando contenido del curso', 'loading');

      const requestId = ++state.requestId;
      const endpointBase = root.dataset.courseEndpointBase || '/apps/brillo-nativo/lms/course/';
      state.courseController?.abort();
      state.courseController = new AbortController();
      const courseSignal = state.courseController.signal;

      try {
        const payload = await requestJson(`${endpointBase}${encodeURIComponent(cleanSlug)}`, { signal: courseSignal });
        if (requestId !== state.requestId) return;

        const course = payload?.course;
        if (!course) throw new Error('El curso solicitado no está disponible para esta cuenta.');

        state.progressAvailable = payload?.progressAvailable === true;
        renderCourse(course);
        setStatus('Academia conectada', 'ready');
        setHidden(elements.courseLoading, true);

        const courseSummary = state.courses.find((item) => toText(item?.slug) === cleanSlug);
        if (courseSummary && course?.progress) {
          courseSummary.progress = course.progress;
          renderCourseList();
        }

        const url = new URL(window.location.href);
        url.searchParams.set('course', cleanSlug);
        window.history.replaceState({}, '', url);
      } catch (error) {
        if (error?.name === 'AbortError' || courseSignal.aborted || signal.aborted) return;
        if (requestId !== state.requestId) return;
        setHidden(elements.courseLoading, true);

        if (isLoggedOutError(error)) {
          showLoggedOut();
          return;
        }

        if (isNoAccessError(error)) {
          showNoAccess();
          return;
        }

        showError(error);
      }
    };

    const continueCourse = () => {
      const lessons = Array.from(elements.modules?.querySelectorAll('[data-lesson-slug]') || []);
      const nextLesson = lessons.find((lesson) => lesson.dataset.lessonStatus !== 'completed');

      if (!nextLesson) {
        showNotice('Has completado todas las lecciones publicadas de este curso.', 'success');
        elements.progress?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const module = nextLesson.closest('details');
      if (module) module.open = true;
      nextLesson.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.clearTimeout(state.focusTimer);
      state.focusTimer = window.setTimeout(() => nextLesson.focus({ preventScroll: true }), 420);
    };

    const loadCourses = async () => {
      hidePrimaryStates();
      setHidden(elements.loading, false);
      setStatus('Conectando con tu academia', 'loading');

      state.coursesController?.abort();
      state.courseController?.abort();
      state.coursesController = new AbortController();
      const coursesSignal = state.coursesController.signal;

      try {
        const endpoint = root.dataset.coursesEndpoint || '/apps/brillo-nativo/lms/courses';
        const payload = await requestJson(endpoint, { signal: coursesSignal });

        if (payload?.loggedIn === false) {
          showLoggedOut();
          return;
        }

        const courses = toArray(payload?.courses);
        if (toNumber(payload?.accessCount) <= 0 || courses.length === 0) {
          showNoAccess();
          return;
        }

        state.progressAvailable = payload?.progressAvailable === true;
        state.courses = courses.filter((course) => toText(course?.slug));
        hidePrimaryStates();
        setHidden(elements.workspace, false);
        renderCourseList();

        const requestedSlug = new URL(window.location.href).searchParams.get('course') || '';
        const initialCourse = state.courses.find((course) => course.slug === requestedSlug) || state.courses[0];
        await loadCourse(initialCourse.slug);
      } catch (error) {
        if (error?.name === 'AbortError' || coursesSignal.aborted || signal.aborted) return;
        if (isLoggedOutError(error)) {
          showLoggedOut();
          return;
        }

        if (isNoAccessError(error)) {
          showNoAccess();
          return;
        }

        showError(error);
      }
    };

    elements.retry?.addEventListener('click', loadCourses, { signal });
    elements.continueButton?.addEventListener('click', continueCourse, { signal });

    runtimes.set(root, {
      destroy() {
        lifecycleController.abort();
        state.coursesController?.abort();
        state.courseController?.abort();
        window.clearTimeout(state.noticeTimer);
        window.clearTimeout(state.focusTimer);
        state.savingLessons.clear();
        delete root.dataset.bnLmsRealMounted;
        runtimes.delete(root);
      },
    });

    loadCourses();
  };

  const init = () => {
    document.querySelectorAll('[data-bn-lms-real]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    const roots = [];
    if (scope?.matches?.('[data-bn-lms-real]')) roots.push(scope);
    scope?.querySelectorAll?.('[data-bn-lms-real]').forEach((root) => roots.push(root));
    roots.forEach((root) => runtimes.get(root)?.destroy());
  });
})();
